import { FC } from 'react';
import {
  LandingPagePreviews,
  LandingPageModuleType,
  LandingPageModuleTypes,
  ChannelTab,
  LandingPageModule,
} from '../../../../models/channels/types';
import AboutPreview from '../preview/AboutPreview';
import MembersPreview from '../preview/MembersPreview';
import PostsPreview from '../preview/PostsPreview';
import ShortcutsPreview from '../preview/ShortcutsPreview';
import CustomPagePreview from '../preview/CustomPagePreview';
import StatsPreview from '../preview/StatsPreview';
import styles from './styles.module.css';

export interface CardProps {
  topicId: string;
}

export interface CustomPageCardProps extends CardProps {
  customPageId: string;
  customPageTitle: string;
  tabId: number;
}

type StandardModuleType = Exclude<
  LandingPageModuleType,
  | typeof LandingPageModuleTypes.Custom
  | typeof LandingPageModuleTypes.TopicStatistics
>;

const PreviewCards: Record<StandardModuleType, FC<CardProps>> = {
  [LandingPageModuleTypes.About]: AboutPreview,
  [LandingPageModuleTypes.Posts]: PostsPreview,
  [LandingPageModuleTypes.Shortcuts]: ShortcutsPreview,
  [LandingPageModuleTypes.Members]: MembersPreview,
};

interface Props {
  landingPagePreviews: LandingPagePreviews;
  topicId: string;
  currentTabId: ChannelTab['id'];
  tabs: ChannelTab[];
}

export const Sidebar: FC<Props> = ({
  currentTabId,
  landingPagePreviews,
  topicId,
  tabs,
}) => {
  const getTabTitle = (tabId: number): string => {
    const tab = tabs.find((t) => t.id === tabId);
    return tab?.title || 'Untitled Page';
  };

  const renderModulePreview = (module: LandingPageModule, index: number) => {
    if (
      !module.enabled ||
      (module.content_channel_tab_id === currentTabId &&
        module.type !== LandingPageModuleTypes.TopicStatistics) // Topic statistics is shown even if we are in the posts tab
    ) {
      return null;
    }

    switch (module.type) {
      case LandingPageModuleTypes.Custom:
        return (
          <CustomPagePreview
            key={`${module.type}-${index}`}
            topicId={topicId}
            customPageId={module.custom_page_id!}
            customPageTitle={getTabTitle(module.content_channel_tab_id)}
            tabId={module.content_channel_tab_id}
          />
        );

      case LandingPageModuleTypes.TopicStatistics:
        return (
          <StatsPreview key={`${module.type}-${index}`} topicId={topicId} />
        );

      default: {
        const Card = PreviewCards[module.type as StandardModuleType];
        return <Card topicId={topicId} key={`${module.type}-${index}`} />;
      }
    }
  };

  if (
    !landingPagePreviews.enabled ||
    landingPagePreviews.modules.length === 0
  ) {
    return null;
  }

  return (
    <div className={styles.sidebar}>
      {landingPagePreviews.modules.map(renderModulePreview)}
    </div>
  );
};
